<template>
  <div>
    <div id="trend-plot-grid">
      <div id="percent-change-plot" ref="thediv">
        <svg
          class="percent-chart"
          ref="chartSVG"
          viewBox="0 0 272.3 88.3"
          preserveAspectRatio="none"
        >
          <path class="target" :d="targetPoints" />
          <path
            v-for="line in yGridLines"
            :key="line"
            stroke="#B7B7B7"
            vector-effect="non-scaling-stroke"
            class="grid-line"
            :d="line"
          />
          <path
            v-if="baselineSignals"
            stroke="#373737"
            class="baseline-line"
            :d="baselinePoints"
          />
          <path
            v-for="line in lines"
            :key="line.color"
            :stroke="line.color"
            class="trend-line"
            :d="line.points"
          />
        </svg>
      </div>
      <div id="y-scale-container">
        <svg class="y-scale-svg" viewBox="0 0 10 100">
          <text
            v-for="tick in ticks"
            :key="tick.id"
            x="7"
            :y="tick.y"
            font-size="7"
            text-anchor="end"
          >
            {{ tick.text }}
          </text>
        </svg>
      </div>
      <svg
        id="trend-title-text"
        viewBox="0 0 20.9 1"
        preserveAspectRatio="xMinYMin"
      >
        <text font-size="0.60pt" x="0.25" y="0.90" fill="Black">
          {{ title }}
        </text>
      </svg>
    </div>
  </div>
</template>

<script>
import { markersMixin } from '@/components/MarkersMixin.js'

export default {
  name: 'ReviewPlot',
  mixins: [markersMixin],
  data() {
    return {
      lines: [],
      canvasHeight: 88.3,
      canvasWidth: 272.3
    }
  },
  props: {
    startDate: {
      type: Date
    },
    endDate: {
      type: Date
    },
    xMin: {
      type: Number,
      required: true
    },
    xMax: {
      type: Number,
      required: true
    },
    signals: {
      type: Array,
      required: true
    },
    markers: {
      type: Array,
      required: false
    },
    targetMin: {
      type: Number,
      default: null
    },
    targetMax: {
      type: Number,
      default: null
    },
    baselineSignals: {
      type: Boolean,
      default: false
    },
    baselineIndex: {
      type: Number,
      default: 0
    },
    title: {
      type: String,
      default: ''
    },
    yTicks: {
      type: Array,
      required: false
    }
  },
  computed: {
    ticks() {
      return this.yTicks.map(y => {
        // 1.5 is offset for text height to center text
        let pos = 100 - ((y - this.yMin) / (this.yMax - this.yMin)) * 100 + 1.5
        return { text: y.toFixed(0), y: pos, id: y }
      })
    },
    xTicks() {
      if (this.startDate == null || this.endDate == null) return
      let plotDurationMins = (this.endDate - this.startDate) / (1000 * 60)
      let chunkSize = 60
      let plotDurationChunks = plotDurationMins / chunkSize
      let baselineDate = new Date(
        this.startDate.getFullYear(),
        this.startDate.getMonth(),
        this.startDate.getDate(),
        this.startDate.getHours(),
        0,
        0
      )
      let res = []
      for (let i = 0; i <= plotDurationChunks; i++) {
        let newDate = new Date(
          baselineDate.getTime() + chunkSize * 60 * 1000 * i
        )
        if (newDate >= this.startDate && newDate <= this.endDate)
          res.push(newDate)
      }
      return res.map(date => {
        return {
          text:
            String(date.getHours()).padStart(2, '0') +
            ':' +
            String(date.getMinutes()).padStart(2, '0'),
          x:
            ((date - this.startDate) / (this.endDate - this.startDate)) * 100 +
            3.2,
          id: date
        }
      })
    },
    percentSignal() {
      return this.percentCombo
    },
    percentCombo() {
      return this.signals.map(signal => {
        return {
          points: this.baselineSignals
            ? this.convertToPercent(signal.points)
            : signal.points,
          color: signal.color
        }
      })
    },
    yMagnitude() {
      //todo: check max over all displayed signals not just first in list, to handle comboSignal
      let maxAbs = 50
      this.percentSignal.forEach(signal => {
        let signalMin = Math.abs(this.signalMin(signal.points))
        let signalMax = Math.abs(this.signalMax(signal.points))
        if (signalMin > maxAbs) maxAbs = signalMin
        if (signalMax > maxAbs) maxAbs = signalMax
      })
      return maxAbs
    },
    maxPercent() {
      return '+' + Math.round(this.yMax) + '%'
    },
    minPercent() {
      return Math.round(this.yMin) + '%'
    },
    currentColor() {
      return '#1B77D2'
    },
    targetPoints() {
      if (this.targetMin == null || this.targetMax == null) return null
      let upperY =
        this.canvasHeight -
        ((this.targetMax - this.yMin) / (this.yMax - this.yMin)) *
          this.canvasHeight
      let lowerY =
        this.canvasHeight -
        ((this.targetMin - this.yMin) / (this.yMax - this.yMin)) *
          this.canvasHeight
      return `M0 ${lowerY} L${this.canvasWidth} ${lowerY} L${this.canvasWidth} ${upperY} L0 ${upperY} Z`
    },
    baselinePoints() {
      let baseline = 100
      let y =
        this.canvasHeight -
        ((baseline - this.yMin) / (this.yMax - this.yMin)) * this.canvasHeight
      return `M0 ${y} L${this.canvasWidth} ${y}`
    },
    yGridLines() {
      return this.yTicks.map(y => {
        var y =
          this.canvasHeight -
          ((y - this.yMin) / (this.yMax - this.yMin)) * this.canvasHeight
        return `M0 ${y} L${this.canvasWidth} ${y}`
      })
    }
  },
  methods: {
    renderSignal() {
      let downsampSig = []
      this.lines = this.percentSignal.map(signal => {
        downsampSig = this.downsample(signal.points, 100)
        let scaledSig = this.scaleSignal(downsampSig)
        return {
          points: this.signalToPoints(scaledSig),
          color: signal.color
        }
      })
      if (this.markers != null) {
        var adjustedMarkers = this.markers.map(marker => {
          let y = this.getClosestYValInArray(marker.x, downsampSig)
          return {
            point: [marker.x, y],
            type: marker.type,
            shortName: marker.shortName,
            isFlipped: false
          }
        })
      }
      this.markersUpdate(this.$refs['chartSVG'], adjustedMarkers)
    },
    downsample(signal, nPoints) {
      if (signal.length <= nPoints) {
        return signal
      }
      let xPerPoint = (signal[signal.length - 1][0] - signal[0][0]) / nPoints
      let windowEnd = signal[0][0] + xPerPoint
      let downsampSig = []
      let xMean = 0
      let yMean = 0
      let count = 0
      downsampSig.push(signal[0])
      for (let i = 1; i < signal.length - 1; i++) {
        if (Number.isNaN(signal[i][1])) continue
        xMean += signal[i][0]
        yMean += signal[i][1]
        count++
        if (signal[i][0] > windowEnd) {
          downsampSig.push([xMean / count, yMean / count])
          xMean = yMean = count = 0
          windowEnd = (Math.floor(signal[i][0] / xPerPoint) + 1) * xPerPoint
        }
      }
      downsampSig.push(signal[signal.length - 1])
      return downsampSig
    },
    scaleSignal(signal) {
      return signal.map(el => [
        this.canvasWidth * ((el[0] - this.xMin) / (this.xMax - this.xMin)),
        this.canvasHeight -
          this.canvasHeight * ((el[1] - this.yMin) / (this.yMax - this.yMin))
      ])
    },
    signalToPoints(signal) {
      let points = ''
      let firstPoint = true
      for (const point of signal) {
        if (isNaN(point[1])) {
          firstPoint = true
        } else if (firstPoint) {
          points += `M${point[0]} ${point[1]} `
          firstPoint = false
        } else {
          points += `L${point[0]} ${point[1]} `
        }
      }
      return points
    },
    convertToPercent(signal) {
      let baseline = this.signalBaseline(signal)
      return signal.map(point => {
        let percent = ((point[1] - baseline) / baseline) * 100 + 100
        return [point[0], percent]
      })
    },
    signalBaseline(signal) {
      return signal[this.baselineIndex][1]
      for (let i = 0; i < signal.length; i++) {
        if (!isNaN(signal[i][1])) {
          return signal[i][1]
        }
      }
      return NaN
    },
    selected(button) {
      this.currentSignal = button
    },
    signalMin(signal) {
      let min = 0
      signal.forEach(element => {
        if (isFinite(element[1]) && element[1] < min) {
          min = element[1]
        }
      })
      return min
    },
    signalMax(signal) {
      let max = 0
      signal.forEach(element => {
        if (isFinite(element[1]) && element[1] > max) {
          max = element[1]
        }
      })
      return max
    },
    getClosestYValInArray(searchTime, array) {
      if (searchTime == null || array == null) return
      // Returns y-value in array that is closest to searchTime
      let idx = 0
      let diff = Math.abs(searchTime - array[0][0])
      for (let i = 1; i < array.length; i++) {
        if (Math.abs(searchTime - array[i][0]) < diff && !isNaN(array[i][1])) {
          diff = Math.abs(searchTime - array[i][0])
          idx = i
        }
      }
      return array[idx][1]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.renderSignal()
    })
  },
  watch: {
    baselineIndex() {
      this.renderSignal()
    },
    signals() {
      this.renderSignal()
    },
    markers() {
      this.renderSignal()
    }
  }
}
</script>

<style scoped>
#trend-plot-grid {
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: 100%;
  grid-template-columns: 6% 94%;
}
#trend-title-text {
  grid-row: 1;
  grid-column-start: 2;
  grid-column-end: 3;
  width: 100%;
}
#percent-change-plot {
  grid-row: 1;
  grid-column: 2;
  background-color: #eeeeee;
  border: #999999 1px solid;
}
#y-scale-container {
  grid-row: 1;
  grid-column: 1;
  position: relative;
}
.y-scale-svg {
  position: absolute;
  height: 100%;
  box-sizing: border-box;
  overflow: visible;
}
.percent-chart {
  display: block;
  height: 100%;
  width: 100%;
}
.trend-line {
  stroke-width: 2;
  fill: none;
}
.baseline-line {
  stroke-width: 2;
  fill: none;
  stroke-dasharray: 5 5;
}
.grid-line {
  stroke-width: 1px;
  stroke-dasharray: 2 2;
}
.target {
  stroke: none;
  fill: #c5eecd;
}
</style>
